import React, { useCallback, useEffect, useState } from "react";
import {
  ArrowTopRightIcon,
  ArrowBottomRightIcon,
  TrashIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import { useCollection } from "@/hooks/useCollection";
import { useFirestore } from "@/hooks/useFirestore";
import { useAuthContext } from "@/hooks/useAuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcn/components/ui/alert-dialog";
import { Input } from "@/shadcn/components/ui/input";
import { useDocument } from "@/hooks/useDocument";
import { debounce } from "lodash";
import { Skeleton } from "@/shadcn/components/ui/skeleton";
import { Separator } from "@/shadcn/components/ui/separator";
import Loading from "@/components/Loading";

import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/shadcn/components/ui/button";
import useIsPWA from "@/hooks/useIsPWA";
import { useNavigate } from "react-router-dom";
import Subscriptions from "../Subscriptions/Subscriptions";
import {
  ChartNoAxesCombinedIcon,
  CircleFadingArrowUpIcon,
  ClockAlertIcon,
  InfoIcon,
  PencilIcon,
} from "lucide-react";
import useMediaQuery from "@/hooks/useMediaQuery";
import { increment } from "firebase/firestore";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip";
import { TutorialDialog } from "../Training/TutorialDialog";

export default function Home() {
  const { user } = useAuthContext();

  const navigate = useNavigate();

  const { documents: progressDocs } = useCollection("progress", [
    "userId",
    "==",
    user.uid,
  ]);

  // const { document: currentAppVersion } = useDocument("appVersion", "current");
  const [accountNumber, setAccountNumber] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  // const marketRiskOn = currentAppVersion?.marketRiskOn;
  const isMobile = useMediaQuery("(max-width: 640px)");
  const isPwa = useIsPWA() && isMobile;
  const [activeTab, setActiveTab] = useState("trades");
  const [balance, setBalance] = useState("");
  const { document: maiaDoc } = useDocument("maia", "rentability");
  const [transactionValue, setTransactionValue] = useState("");
  const { document: userDoc } = useDocument("users", user.uid);
  const { updateDocument: updateUser } = useFirestore("users");

  const { addDocument: addTransaction } = useFirestore("transactions");

  const updateBalanceInDB = async (value, action) => {
    value = Number(value);
    if (action === "withdrawal") value = value * -1;

    try {
      await updateUser(user.uid, {
        balance: action ? increment(value) : value,
      });
    } catch (error) {
      console.error("Error updating balance: ", error);
    }
  };

  const debouncedUpdateBalance = useCallback(
    debounce((value, action) => updateBalanceInDB(value, action), 2000),
    []
  );

  const changeBalance = (value, action) => {
    console.log(value);
    value = value.replace(",", ".");
    if (value.split(".").length > 2) {
      value = value.replace(".", "");
    }
    if (value.split(".").length === 2 && value.split(".")[1].length > 2) {
      console.log("entrou aqui");
      value = value.split(".")[0] + "." + value.split(".")[1].substring(0, 2);
      console.log(value);
    }
    if (value.length && value[0] === ".") {
      value = value.replace(".", "");
    }
    if (action) {
      setTransactionValue("");
      debouncedUpdateBalance(value, action);
    } else {
      setBalance(value);
      debouncedUpdateBalance(value);
    }
  };

  const saveTransaction = async (transactionValue, action) => {
    await addTransaction({
      ownerId: user.uid,
      type: action,
      value: transactionValue,
      createdAt: new Date(),
    });

    changeBalance(transactionValue, action);
    setTransactionValue("");
  };

  const getAccountLevel = (balance) => {
    if (balance < 300) {
      return "👶 Baby Money";
    } else if (balance < 1000) {
      return "🤑 Money Maker";
    } else if (balance < 5000) {
      return "💎 Money Expert";
    } else if (balance < 10000) {
      return "🎩 Money Master";
    } else if (balance > 9999) {
      return "👑 Money King";
    }
  };

  const saveAccountNumber = async () => {
    setIsEditing(false);
    await updateUser(user.uid, {
      accountNumber,
    });
  };

  useEffect(() => {
    if (userDoc) {
      setBalance(userDoc.balance || "");
      setAccountNumber(userDoc.accountNumber || "");
    }
  }, [userDoc]);

  useEffect(() => {
    if (location.search === "?t=trades") {
      setActiveTab("trades");
    } else if (location.search === "?t=wallet") {
      setActiveTab("wallet");
    }
  }, [location.search]);

  // const openTrades = tradesDocs?.filter((tr) => !tr.closedAt);

  if (!userDoc || !progressDocs) return <Loading />;

  const purchaseDate = userDoc.plan.purchase_date.toDate();
  // User should have 3 days after purchasing to link their account
  const threeDaysAfterPurchase = new Date(purchaseDate);
  threeDaysAfterPurchase.setDate(threeDaysAfterPurchase.getDate() + 3);
  let daysLeft = Math.ceil(
    (threeDaysAfterPurchase - new Date()) / (1000 * 60 * 60 * 24)
  );

  if (daysLeft < 0) daysLeft = 3;

  if (userDoc.plan.status !== "active") return <Subscriptions />;

  // if (!currentAppVersion) return <Loading />;

  if (progressDocs.length !== 10) {
    return (
      <div className="flex flex-col gap-y-4 sm:gap-y-8 min-h-[calc(100vh_-_96px)] sm:w-1/2">
        <h1 className="text-3xl font-semibold text-icon tracking-wide">
          MA<span className="text-primary">.</span>IA
          <span className="text-primary">™</span>
        </h1>
        <div
          className="text-[13px] sm:text-[15px] mt-2 sm:mt-0 bg-red-500/5 border border-red-500 rounded-md p-3 sm:p-6 cursor-pointer"
          onClick={() => {
            if (userDoc.meaningfulActions.clickedAffiliateLink.status) {
              navigate("/maia");
            } else {
              navigate("/assistir");
            }
          }}
        >
          <p className="text-red-500">
            Você só tem mais <b>{daysLeft} dias</b> para ativar a MA.IA™
            gratuitamente.
          </p>
          <p className="mt-1.5 text-red-500">
            Complete a unidade <b>Primeiros Passos</b> para não perder seu
            bônus.
          </p>
        </div>
        <div className="mt-2.5 sm:mt-0">
          <div className="flex gap-2.5 mb-1">
            <ChartNoAxesCombinedIcon className="text-icon" />
            <h1 className="text-xl font-medium text-black/75">Rentabilidade</h1>
          </div>
          <p className="text-xs text-muted-foreground mb-2.5">
            Confira o lucro da MA.IA™ nos últimos dias.
          </p>
          <div className="mt-2.5 flex items-center sm:gap-5 border p-3 sm:p-6 bg-white rounded-md cursor-pointer">
            <div className="flex flex-wrap justify-between gap-y-1.5 sm:gap-y-3 w-full">
              {maiaDoc ? (
                Object.keys(maiaDoc)
                  .sort()
                  .map((key) => {
                    if (key !== "id") {
                      return (
                        <p
                          key={key}
                          className={`${
                            key === "hoje" || key === "last07Days"
                              ? "w-1/2"
                              : "w-1/4"
                          }  text-muted-foreground text-[13px] sm:text-[15px] ${
                            maiaDoc[key].display
                          }`}
                        >
                          {maiaDoc[key].label}:{" "}
                          <span className="text-green-500 font-medium">
                            +{maiaDoc[key].value}
                          </span>
                        </p>
                      );
                    }
                  })
              ) : (
                <p>Carregando...</p>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2 sm:mt-0 text-sm flex flex-col gap-y-2">
          <div>
            Você ainda não concluiu a unidade <b>Primeiros Passos</b>.
          </div>
          <p>
            Para liberar seu acesso a esta área, assista a todas as aulas da
            unidade <b>Primeiros Passos</b>, e certifique-se de que todas as
            aulas estão marcadas como <b>Concluída</b>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:gap-y-8 min-h-[calc(100vh_-_96px)] px-2.5 sm:px-0">
      <h1 className="text-3xl font-semibold text-icon tracking-wide">
        MA<span className="text-primary">.</span>IA
        <span className="text-primary">™</span>
      </h1>

      <div className="mt-4 sm:mt-0 sm:w-1/2">
        <div className="flex items-center gap-2.5">
          <p>Informe o número da sua conta de investidor.</p>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon className="w-4 h-4" />
              </TooltipTrigger>
              <TooltipContent className="p-2.5">
                <div className="flex flex-col gap-1">
                  <p>Seu número de conta de investidor na XM, com 8 dígitos.</p>
                  <p>
                    <b>Ex:</b> 12345678
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="mt-1.5 flex items-center gap-2.5">
          <Input
            disabled={userDoc.accountNumber && !isEditing}
            value={accountNumber}
            onChange={(e) =>
              setAccountNumber(e.target.value.replace(/[^0-9]/g, ""))
            }
            className="w-40 h-8 py-0 pl-2.5 pr-0"
          />
          {(!userDoc.accountNumber || isEditing) && (
            <Button
              disabled={accountNumber.length !== 8}
              size="xs"
              onClick={saveAccountNumber}
            >
              Confirmar
            </Button>
          )}
          {userDoc.accountNumber && !isEditing && (
            <Button
              disabled={accountNumber.length !== 8}
              size="smallIcon"
              onClick={() => setIsEditing(true)}
            >
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="mt-2.5">
          {accountNumber.length && accountNumber.length !== 8 ? (
            <p>
              <span className="text-red-500 text-sm">
                O número da conta deve ter 8 dígitos.
              </span>
            </p>
          ) : null}
          <p className="pt-2.5 text-[14px]">
            Ainda não abriu a conta de investidor?
          </p>
          <TutorialDialog tutorialName="CopyTrading">
            <div
              role="button"
              onClick={async () => {
                const meaningfulActions = userDoc.meaningfulActions || {};
                const copyTutorial = meaningfulActions.copyTutorial || {};

                if (copyTutorial.open?.status) {
                  return;
                }

                await updateUser(user.uid, {
                  meaningfulActions: {
                    ...meaningfulActions,
                    copyTutorial: {
                      ...copyTutorial,
                      open: {
                        status: true,
                        status_date: new Date().getTime(),
                      },
                      step: 1,
                    },
                  },
                });
              }}
            >
              <p className="text-icon underline text-[14px]">
                Clique aqui para ver o tutorial
              </p>
            </div>
          </TutorialDialog>
        </div>

        <Separator className="my-5 sm:my-8" />

        <div className="rounded-md flex flex-col gap-2.5">
          <div className="flex justify-between sm:justify-start sm:gap-36">
            <div className="flex justify-between sm:justify-start sm:gap-36 sm:px-0">
              <div className="flex flex-col justify-center gap-1.5">
                <p>Meu capital</p>
                <div className="flex gap-1 items-center font-semibold">
                  <p className="text-xl sm:text-lg">US$</p>
                  <Input
                    className={`${
                      balance
                        ? "text-xl sm:text-lg font-semibold"
                        : "text-sm font-normal"
                    } w-20 h-8 py-0 pl-0 pr-0 border-none text-left focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent focus-visible:ring-transparent`}
                    type="text"
                    maxLength={7}
                    placeholder="Informe"
                    value={balance}
                    onChange={(e) =>
                      changeBalance(e.target.value.replace(/[^0-9/.,]/g, ""))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm">Registrar depósito</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Informe o valor depositado.
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Digite o valor em dólares que você depositou na corretora.
                    </AlertDialogDescription>
                    <p className="text-foreground font-light text-sm text-left mb-1">
                      Valor do depósito
                    </p>
                    <div className="px-2.5 border border-input rounded-md flex gap-1 items-center">
                      <p>US$</p>
                      <Input
                        className="border-none focus-visible:ring-transparent p-0 text-md focus-visible:ring-offset-0"
                        placeholder="Informe o valor"
                        maxLength={6}
                        value={transactionValue}
                        onChange={(e) =>
                          setTransactionValue(
                            e.target.value.replace(/[^0-9.]/g, "")
                          )
                        }
                      />
                    </div>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-green-500 hover:bg-green-600 font-semibold"
                      onClick={() =>
                        saveTransaction(transactionValue, "deposit")
                      }
                    >
                      Confirmar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    Registrar saque
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Informe o valor do saque.
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Digite o valor em dólares que você sacou da corretora.
                    </AlertDialogDescription>
                    <p className="text-foreground font-light text-sm text-left mb-1">
                      Valor do saque
                    </p>
                    <div className="px-2.5 border border-input rounded-md flex gap-1 items-center">
                      <p>US$</p>
                      <Input
                        className="border-none focus-visible:ring-transparent p-0 text-md focus-visible:ring-offset-0"
                        placeholder="Informe o valor"
                        maxLength={6}
                        value={transactionValue}
                        onChange={(e) =>
                          setTransactionValue(
                            e.target.value.replace(/[^0-9.]/g, "")
                          )
                        }
                      />
                    </div>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-green-500 hover:bg-green-600 font-semibold"
                      onClick={() =>
                        saveTransaction(transactionValue, "withdrawal")
                      }
                    >
                      Confirmar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          <Separator className="my-5 sm:my-8" />
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2.5">
              <p className="">Nível de conta</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <InfoIcon className="w-4 h-4" />
                  </TooltipTrigger>
                  <TooltipContent className="p-5">
                    <div className="flex flex-col gap-2.5">
                      <p>
                        <b>👶 Baby Money:</b> até $300
                      </p>
                      <p>
                        <b>🤑 Money Maker:</b> De $300 a $1.000
                      </p>
                      <p>
                        <b>💎 Money Expert</b> De $1.000 a $5.000
                      </p>
                      <p>
                        <b>🎩 Money Master:</b> De $5.000 a $10.000
                      </p>
                      <p>
                        <b>👑 Money King:</b> Acima de $10.000
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="font-semibold text-xl sm:text-lg">
              {getAccountLevel(balance)}
            </p>
          </div>
          <Separator className="my-5 sm:my-8" />
        </div>
      </div>
    </div>
  );
}
